import { useState, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Key, Plus, Trash2, Power, PowerOff, Copy, Eye, EyeOff,
  RefreshCw, Shield, Clock, AlertTriangle, Settings, X, Check
} from "lucide-react";

// ─── Permission Labels ────────────────────────────────────────────────────────
const PERM_LABELS = {
  read_solutions:      "Çözüm metinlerini oku",
  write_questions:     "Soru güncelleme (write_questions)",
  write_question_media:"V1 medya yazma (write_question_media)",
};
const MEDIA_V2_LABELS = {
  upload_media:             "Medya yükle (upload_media)",
  publish_question_media:   "Medya yayınla (publish_question_media)",
  manage_media_permissions: "İzin yönetimi (manage_media_permissions)",
};

// ─── Permissions Edit Modal ───────────────────────────────────────────────────
function PermissionsModal({ apiKey, onClose, onSaved }) {
  const src = apiKey.permissions || {};
  const srcV2 = src.media_v2 || {};

  const [draft, setDraft] = useState({
    read_solutions:       !!src.read_solutions,
    write_questions:      !!src.write_questions,
    write_question_media: !!src.write_question_media,
    media_v2: {
      upload_media:             !!srcV2.upload_media,
      publish_question_media:   !!srcV2.publish_question_media,
      manage_media_permissions: !!srcV2.manage_media_permissions,
    },
  });
  const [saving, setSaving] = useState(false);

  const setTop = (field, val) =>
    setDraft(d => ({ ...d, [field]: val }));
  const setV2 = (field, val) =>
    setDraft(d => ({ ...d, media_v2: { ...d.media_v2, [field]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/api-keys/${apiKey.id}/permissions`, {
        permissions: draft,
      });
      toast.success("İzinler güncellendi");
      onSaved(res.data.permissions);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "İzinler güncellenemedi";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="permissions-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Settings size={17} className="text-blue-600" />
            <span className="font-semibold text-gray-800 text-sm">İzinleri Düzenle</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            data-testid="permissions-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Key info */}
        <div className="px-5 pt-3 pb-1">
          <p className="text-xs text-gray-500">
            Anahtar: <span className="font-medium text-gray-700">{apiKey.name}</span>
            <code className="ml-2 text-[11px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
              {apiKey.key_prefix}
            </code>
          </p>
        </div>

        {/* Permissions */}
        <div className="px-5 py-4 space-y-4">
          {/* Top-level */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Genel İzinler</p>
            {Object.entries(PERM_LABELS).map(([field, label]) => (
              <label
                key={field}
                className="flex items-center gap-3 cursor-pointer group"
                data-testid={`perm-toggle-${field}`}
              >
                <span
                  className={`w-4 h-4 flex items-center justify-center rounded border transition-colors ${
                    draft[field]
                      ? "bg-blue-600 border-blue-600"
                      : "bg-white border-gray-300 group-hover:border-blue-400"
                  }`}
                  onClick={() => setTop(field, !draft[field])}
                >
                  {draft[field] && <Check size={11} className="text-white" />}
                </span>
                <span className="text-sm text-gray-700 select-none">{label}</span>
              </label>
            ))}
          </div>

          {/* Media V2 */}
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Media V2</p>
            {Object.entries(MEDIA_V2_LABELS).map(([field, label]) => (
              <label
                key={field}
                className="flex items-center gap-3 cursor-pointer group"
                data-testid={`perm-toggle-media_v2.${field}`}
              >
                <span
                  className={`w-4 h-4 flex items-center justify-center rounded border transition-colors ${
                    draft.media_v2[field]
                      ? "bg-blue-600 border-blue-600"
                      : "bg-white border-gray-300 group-hover:border-blue-400"
                  }`}
                  onClick={() => setV2(field, !draft.media_v2[field])}
                >
                  {draft.media_v2[field] && <Check size={11} className="text-white" />}
                </span>
                <span className="text-sm text-gray-700 select-none">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <p className="text-[10px] text-gray-400">
            Değiştirilmeyen izinler korunur
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              İptal
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              data-testid="permissions-save-btn"
            >
              {saving ? <RefreshCw size={13} className="animate-spin" /> : "Kaydet"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);   // key being edited in modal

  const fetchKeys = async () => {
    try {
      const res = await api.get("/admin/api-keys");
      setKeys(res.data.keys || []);
    } catch {
      toast.error("API anahtarları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleCreate = async () => {
    if (!newKeyName.trim()) { toast.error("Anahtar adı gerekli"); return; }
    setCreating(true);
    try {
      const res = await api.post("/admin/api-keys", { name: newKeyName.trim() });
      setRevealedKey({ id: res.data.id, key: res.data.key, name: res.data.name });
      setNewKeyName("");
      setShowCreateForm(false);
      await fetchKeys();
      toast.success("API anahtarı oluşturuldu");
    } catch {
      toast.error("Anahtar oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (keyId, current) => {
    try {
      await api.patch(`/admin/api-keys/${keyId}`, { is_active: !current });
      toast.success(current ? "Anahtar devre dışı bırakıldı" : "Anahtar aktif edildi");
      fetchKeys();
    } catch {
      toast.error("Güncelleme başarısız");
    }
  };

  const handleDelete = async (keyId, name) => {
    if (!window.confirm(`"${name}" anahtarını kalıcı olarak silmek istediğinizden emin misiniz?`)) return;
    try {
      await api.delete(`/admin/api-keys/${keyId}`);
      toast.success("Anahtar silindi");
      fetchKeys();
    } catch {
      toast.error("Silme başarısız");
    }
  };

  const handlePermissionsSaved = (newPerms) => {
    // Local state'i güncelle — refetch gerekmez
    setKeys(prev =>
      prev.map(k => k.id === editingKey.id ? { ...k, permissions: newPerms } : k)
    );
  };

  const copyToClipboard = async (text, label = "Anahtar") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopyalandı`);
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("tr-TR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    } catch { return iso; }
  };

  // İzin badge özeti (en fazla 3 etkin izin göster)
  const permBadges = (perms = {}) => {
    const active = [];
    if (perms.write_questions) active.push("write_questions");
    if (perms.media_v2?.upload_media) active.push("upload_media");
    if (perms.media_v2?.publish_question_media) active.push("publish_media");
    if (perms.write_question_media) active.push("write_media");
    if (perms.media_v2?.manage_media_permissions) active.push("manage_perms");
    return active;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Permissions Modal */}
      {editingKey && (
        <PermissionsModal
          apiKey={editingKey}
          onClose={() => setEditingKey(null)}
          onSaved={handlePermissionsSaved}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Shield size={22} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Distribution API Anahtarları</h1>
            <p className="text-sm text-gray-500 mt-0.5">Harici platformların erişim anahtarlarını yönetin</p>
          </div>
        </div>
        <Button
          data-testid="create-api-key-btn"
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2"
        >
          <Plus size={16} /> Yeni Anahtar
        </Button>
      </div>

      {/* API Base URL info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm font-mono text-gray-700 space-y-1">
        <div className="text-xs text-gray-400 font-sans mb-2 font-medium">Distribution API Endpoint'leri</div>
        {[
          "GET  /api/v1/dist/questions",
          "GET  /api/v1/dist/questions/by-id/{id}",
          "GET  /api/v1/dist/questions/by-code/{code}",
          "POST /api/v1/dist/questions/batch",
          "GET  /api/v1/dist/taxonomy",
          "GET  /api/v1/dist/sync?since=2024-01-01T00:00:00Z",
        ].map(ep => (
          <div key={ep} className="text-xs text-gray-600">{ep}</div>
        ))}
        <div className="pt-2 text-xs text-gray-400 font-sans">
          Her istekte <code className="bg-gray-200 px-1 rounded">X-API-Key: qb_live_...</code> header'ı gereklidir.
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 flex items-center gap-3"
          data-testid="create-key-form">
          <Key size={18} className="text-blue-500 shrink-0" />
          <input
            type="text"
            placeholder="Anahtar adı (örn: iOS Uygulaması)"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleCreate()}
            className="flex-1 border border-blue-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            autoFocus
            data-testid="api-key-name-input"
          />
          <Button
            onClick={handleCreate}
            disabled={creating}
            size="sm"
            data-testid="confirm-create-key-btn"
          >
            {creating ? <RefreshCw size={14} className="animate-spin" /> : "Oluştur"}
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => { setShowCreateForm(false); setNewKeyName(""); }}
          >
            İptal
          </Button>
        </div>
      )}

      {/* Revealed Key Banner */}
      {revealedKey && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 space-y-3"
          data-testid="revealed-key-banner">
          <div className="flex items-center gap-2 text-amber-700 font-medium">
            <AlertTriangle size={16} />
            <span>"{revealedKey.name}" anahtarı oluşturuldu — bir kez gösterilir, şimdi kopyalayın</span>
          </div>
          <div className="flex items-center gap-2 bg-white border border-amber-200 rounded px-3 py-2">
            <code className="flex-1 text-sm text-gray-800 font-mono break-all"
              data-testid="new-api-key-value">
              {revealedKey.key}
            </code>
            <Button
              variant="ghost" size="sm"
              onClick={() => copyToClipboard(revealedKey.key, "API Anahtarı")}
              data-testid="copy-new-key-btn"
            >
              <Copy size={14} />
            </Button>
          </div>
          <Button
            variant="ghost" size="sm"
            className="text-amber-600"
            onClick={() => setRevealedKey(null)}
          >
            Anladım, kopyaladım
          </Button>
        </div>
      )}

      {/* Keys Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
          Yükleniyor...
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-16 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
          <Key size={32} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">Henüz API anahtarı yok</p>
          <p className="text-sm mt-1">Yukarıdan yeni bir anahtar oluşturun</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Ad</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Anahtar (öneki)</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Durum</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">İzinler</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Son Kullanım</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {keys.map(k => {
                const badges = permBadges(k.permissions);
                return (
                  <tr key={k.id} className="hover:bg-gray-50 transition-colors"
                    data-testid={`api-key-row-${k.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{k.name}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                          {k.key_prefix}
                        </code>
                        <button
                          onClick={() => copyToClipboard(k.key_prefix, "Anahtar öneki")}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy size={12} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium ${
                        k.is_active
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}>
                        {k.is_active
                          ? <><Power size={10} /> Aktif</>
                          : <><PowerOff size={10} /> Pasif</>
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {badges.length === 0 ? (
                          <span className="text-xs text-gray-400 italic">—</span>
                        ) : badges.map(b => (
                          <span key={b}
                            className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono"
                            data-testid={`perm-badge-${k.id}-${b}`}
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        {formatDate(k.last_used_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setEditingKey(k)}
                          className="p-1.5 rounded hover:bg-blue-50 text-blue-400 hover:text-blue-600 transition-colors"
                          title="İzinleri düzenle"
                          data-testid={`edit-permissions-${k.id}`}
                        >
                          <Settings size={15} />
                        </button>
                        <button
                          onClick={() => handleToggle(k.id, k.is_active)}
                          className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                            k.is_active ? "text-amber-500" : "text-green-500"
                          }`}
                          title={k.is_active ? "Devre dışı bırak" : "Aktif et"}
                          data-testid={`toggle-key-${k.id}`}
                        >
                          {k.is_active ? <PowerOff size={15} /> : <Power size={15} />}
                        </button>
                        <button
                          onClick={() => handleDelete(k.id, k.name)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                          title="Sil"
                          data-testid={`delete-key-${k.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
